import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  ViewChild,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AzTableComponent,
  AzTableColumn,
  AzTableAction,
} from '../../../../shared/components/az-table/az-table.component';
import { AzButtonComponent } from '../../../../shared/components/az-button/az-button.component';
import { AzDocumentUpdateModalComponent } from '../../../../shared/components/az-document-update-modal/az-document-update-modal.component';
import { StatusChangeModalComponent } from '../../../../shared/components/status-change-modal/status-change-modal.component';
import { LoggerService } from '../../../../core/logging/logger.service';
import { LegalEntityService, DocumentFile } from '../../services/legal-entity.service';
import { UserPermissionsService } from '../../../../core/auth/services/user-permissions.service';
import { SpinnerComponent } from '../../../../shared/components/spinner/spinner.component';
import { HttpEventType } from '@angular/common/http';
import { switchMap } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { SelectedLegalEntityService } from '../../../../shared/services/selected-legal-entity.service';

interface DocumentRow {
  document_file_id: number;
  document: string;
  version: number;
  uploadedBy: string;
  uploadDate: string;
  comments: string;
}

@Component({
  selector: 'app-legal-entity-documents',
  standalone: true,
  imports: [
    CommonModule,
    AzTableComponent,
    AzButtonComponent,
    AzDocumentUpdateModalComponent,
    StatusChangeModalComponent,
    SpinnerComponent,
  ],
  templateUrl: './legal-entity-documents.component.html',
  styleUrl: './legal-entity-documents.component.scss',
})
export class LegalEntityDocumentsComponent implements OnInit, OnChanges, OnDestroy {
  private statusChangeSubscription?: Subscription;

  constructor(
    private logger: LoggerService,
    private legalEntityService: LegalEntityService,
    public userPermissions: UserPermissionsService,
    private selectedLegalEntity: SelectedLegalEntityService,
  ) {}

  @Input() entityId: number | null = null;

  @ViewChild(StatusChangeModalComponent) statusModal!: StatusChangeModalComponent;

  documents: DocumentRow[] = [];
  loading: boolean = false;
  uploading: boolean = false;
  isModalOpen: boolean = false;
  isStatusModalOpen: boolean = false;
  documentTypes: string[] = []; // Will be loaded from API

  columns: AzTableColumn[] = [
    {
      key: 'document',
      label: 'Document',
      width: '140px',
    },
    {
      key: 'version',
      label: 'Version',
      width: '80px',
      align: 'center',
    },
    {
      key: 'uploadedBy',
      label: 'Uploaded By',
      width: '115px',
    },
    {
      key: 'uploadDate',
      label: 'Upload Date',
      width: '117px',
    },
    {
      key: 'comments',
      label: 'Comments',
      width: 'auto',
    },
  ];
  actions: AzTableAction<DocumentRow>[] = [
    {
      icon: 'download',
      label: 'Download',
      callback: (row: DocumentRow) => this.downloadDocument(row),
    },
  ];

  /**
   * Getter to check if user has permission to upload documents
   */
  get canUploadDocuments(): boolean {
    return this.userPermissions.hasUploadDocumentPermission();
  }

  ngOnInit(): void {
    this.loadDocuments();
    this.loadDocumentTypes();

    // Subscribe to status change approval events to reload documents
    this.statusChangeSubscription = this.selectedLegalEntity.statusChangeApproved$.subscribe(() => {
      this.logger.info('Status change approved, reloading documents');
      this.loadDocuments();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Reload documents when entityId changes (but not on first init)
    if (changes['entityId'] && !changes['entityId'].firstChange) {
      this.logger.info('Entity ID changed, reloading documents', {
        previousId: changes['entityId'].previousValue,
        currentId: changes['entityId'].currentValue,
      });
      this.loadDocuments();
    }
  }

  ngOnDestroy(): void {
    // Clean up subscription
    this.statusChangeSubscription?.unsubscribe();
  }

  loadDocuments(): void {
    if (!this.entityId) {
      this.logger.warn('No entityId provided, cannot load documents');
      return;
    }

    this.loading = true;
    this.legalEntityService.getLegalEntityDocuments(this.entityId).subscribe({
      next: (documentFiles: DocumentFile[]) => {
        this.documents = this.transformDocuments(documentFiles);
        this.logger.info(`Loaded ${this.documents.length} documents for entity ${this.entityId}`);
        this.loading = false;
      },
      error: (error) => {
        this.logger.error('Error loading documents:', error);
        this.documents = [];
        this.loading = false;
      },
    });
  }

  /**
   * Load available document types from backend
   */
  loadDocumentTypes(): void {
    this.legalEntityService.getDocumentTypes().subscribe({
      next: (types) => {
        // Transform DocumentType[] to string[] for the selector
        this.documentTypes = types.map((type) => type.name);
        this.logger.info(`Loaded ${this.documentTypes.length} document types`);
      },
      error: (error) => {
        this.logger.error('Error loading document types:', error);
        // Fallback to empty array if API fails
        this.documentTypes = [];
      },
    });
  }

  /**
   * Transform DocumentFile from API to DocumentRow for table display
   */
  private transformDocuments(documentFiles: DocumentFile[]): DocumentRow[] {
    return documentFiles.map((doc) => ({
      document_file_id: doc.document_file_id,
      document: doc.document_type,
      version: doc.version,
      uploadedBy: doc.uploaded_by || '--',
      uploadDate: this.formatDate(doc.created_date),
      comments: doc.comments || '--',
    }));
  }

  /**
   * Format Unix timestamp to readable date string (DD/MMM/YYYY)
   */
  private formatDate(timestamp: number): string {
    const date = new Date(timestamp * 1000);
    const day = date.getDate();
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  downloadDocument(row: DocumentRow): void {
    if (!this.entityId) {
      this.logger.error('Cannot download: No entity ID');
      return;
    }

    this.logger.info(`Downloading document: ${row.document} (ID: ${row.document_file_id})`);

    this.legalEntityService.generateDownloadUrl(this.entityId, row.document_file_id).subscribe({
      next: (response) => {
        this.logger.info('Download URL generated successfully');
        // Use the downloadFile helper method to trigger browser download
        this.legalEntityService.downloadFile(response.download_url, response.filename);
      },
      error: (error) => {
        this.logger.error('Error generating download URL:', error);
      },
    });
  }

  onRowClick(_row: DocumentRow): void {
    // TODO: Implement row click behavior (e.g., open document preview)
    // Selected row: row
  }

  openModal(): void {
    this.isModalOpen = true;
  }

  closeModal(): void {
    this.isModalOpen = false;
  }

  closeStatusModal(): void {
    this.isStatusModalOpen = false;
  }

  handleUpload(data: { file: File | null; documentType: string; comments: string }): void {
    if (!this.entityId) {
      this.logger.error('Cannot upload: No entity ID');
      return;
    }

    if (!data.file) {
      this.logger.error('Cannot upload: No file selected');
      return;
    }

    if (!data.documentType) {
      this.logger.error('Cannot upload: No document type selected');
      return;
    }

    this.logger.info('Starting document upload process...', {
      documentType: data.documentType,
      fileName: data.file.name,
      fileSize: data.file.size,
    });

    // Show loading state
    this.uploading = true;

    // Step 1: Generate presigned upload URL
    const uploadRequest = {
      document_type_name: data.documentType,
      comments: data.comments || undefined,
    };

    // Store document_file_id for step 3
    let documentFileId: number;

    this.legalEntityService
      .generateUploadUrl(this.entityId, uploadRequest)
      .pipe(
        switchMap((uploadResponse) => {
          this.logger.info('Presigned URL generated successfully', {
            document_file_id: uploadResponse.document_file_id,
            s3_key: uploadResponse.s3_key,
            expires_in: uploadResponse.expires_in,
          });

          // Store document_file_id for confirmation step
          documentFileId = uploadResponse.document_file_id;

          // Step 2: Upload file to S3 using presigned URL
          return this.legalEntityService.uploadToS3(data.file!, uploadResponse.presigned_post);
        }),
        switchMap((event) => {
          // Track upload progress
          if (event.type === HttpEventType.UploadProgress) {
            const progress = event.total ? Math.round((100 * event.loaded) / event.total) : 0;
            this.logger.info(`Upload progress: ${progress}%`);
            // Return empty observable to continue chain
            return [];
          } else if (event.type === HttpEventType.Response) {
            this.logger.info('File uploaded to S3 successfully');
            // Step 3: Confirm upload to activate document record
            return this.legalEntityService.confirmUpload(documentFileId);
          }
          return [];
        }),
      )
      .subscribe({
        next: (confirmResponse) => {
          // Only log if we have a confirmation response (not progress events)
          if (confirmResponse) {
            this.logger.info('Upload confirmed successfully', confirmResponse);

            // Hide loading state
            this.uploading = false;

            // Close upload modal
            this.closeModal();

            // Show success modal
            this.isStatusModalOpen = true;
            setTimeout(() => {
              if (this.statusModal) {
                this.statusModal.showSuccessWithMessage('Document uploaded successfully!');
              }
            }, 0);

            // Reload documents
            this.loadDocuments();
          }
        },
        error: (error) => {
          this.logger.error('Error during upload process:', error);
          this.uploading = false;

          // Show error in status modal
          this.isStatusModalOpen = true;
          setTimeout(() => {
            if (this.statusModal) {
              this.statusModal.showSuccessWithMessage(
                'Error uploading document. Please try again.',
              );
            }
          }, 0);
        },
      });
  }
}
